import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import UploadPage from "./pages/UploadPage";
import FilesPage from "./pages/FilesPage";
import FileDetailPage from "./pages/FileDetailPage";
import ChatPage from "./pages/ChatPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import DashboardBuilderPage from "./pages/DashboardBuilderPage";
import ReportsListPage from "./pages/ReportsListPage";
import ReportViewPage from "./pages/ReportViewPage";
import ExportIntelligencePage from "./pages/ExportIntelligencePage";
import ExportIntelligenceReact from "./pages/ExportIntelligenceReact";
import CompetitiveIntelligenceReact from "./pages/CompetitiveIntelligenceReact";
import BuyersExplorerReact from "./pages/BuyersExplorerReact";
import CompetitorExplorerReact from "./pages/CompetitorExplorerReact";
import MarketDrilldownReact from "./pages/MarketDrilldownReact";
import WhiteSpacePortsReact from "./pages/WhiteSpacePortsReact";
import InvoicePITrackerPage from "./pages/InvoicePITrackerPage";
import GradeStrategyPage from "./pages/GradeStrategyPage";
import GradeStrategyReact from "./pages/GradeStrategyReact";
import PriceTrendPage from "./pages/PriceTrendPage";
import SolarProjectPage from "./pages/SolarProjectPage";
import MarketWatchPage from "./pages/MarketWatchPage";
import GeneralStoresPage from "./pages/GeneralStoresPage";
import FeedPlantPage from "./pages/FeedPlantPage";
import FeedPlantEntryPage from "./pages/FeedPlantEntryPage";
import CultureStorePage from "./pages/CultureStorePage";
import CultureStoreEntryPage from "./pages/CultureStoreEntryPage";
import KaplStorePage from "./pages/KaplStorePage";
import KaplStoreEntryPage from "./pages/KaplStoreEntryPage";
import RmfgPage from "./pages/RmfgPage";
import RmfgEntryPage from "./pages/RmfgEntryPage";
import PackingStorePage from "./pages/PackingStorePage";
import PackingStoreEntryPage from "./pages/PackingStoreEntryPage";
import SilPipelinePage from "./pages/SilPipelinePage";
import RussiaRegisterPage from "./pages/RussiaRegisterPage";
import StoresEntryPage from "./pages/StoresEntryPage";
import AdminDataOpsPage from "./pages/AdminDataOpsPage";
import ProcurementRatesPage from "./pages/ProcurementRatesPage";
import PriceTrendReact from "./pages/PriceTrendReact";
import ShareButton from "./components/ShareButton";

// Serve dashboards via authenticated backend endpoints
const IntelligenceRedirect = () => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <ShareButton dashboardType="export" />
      <iframe
        src="/api/export-intelligence/view"
        title="Export Intelligence"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

const CompetitiveRedirect = () => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <ShareButton dashboardType="competitive" />
      <iframe
        src="/api/export-intelligence/competitive"
        title="Competitive Intelligence"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

const CombinedRedirect = () => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <ShareButton dashboardType="combined" />
      <iframe
        src="/api/export-intelligence/competitor-explorer-page"
        title="Competitor Explorer"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

const WhiteSpaceRedirect = () => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <ShareButton dashboardType="whitespace" />
      <iframe
        src="/api/export-intelligence/whitespace-ports-page"
        title="White-Space Destination Ports"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

const DrilldownRedirect = () => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <ShareButton dashboardType="drilldown" />
      <iframe
        src="/api/export-intelligence/drilldown-view"
        title="Market Drilldown"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

const BuyersExplorerRedirect = () => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <ShareButton dashboardType="buyers" />
      <iframe
        src="/api/export-intelligence/buyers-explorer-view"
        title="Buyers Explorer"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

// Public shared view (no auth)
const SharedDashboard = () => {
  const params = new URLSearchParams(window.location.pathname.split('/shared/')[1]);
  const token = window.location.pathname.split('/shared/')[1];
  return (
    <iframe
      src={`/api/export-intelligence/public/${token}`}
      title="Shared Dashboard"
      style={{ width: '100vw', height: '100vh', border: 'none', position: 'fixed', top: 0, left: 0 }}
    />
  );
};

// Layout
import DashboardLayout from "./layouts/DashboardLayout";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route wrapper - redirects non-admin users to /reports
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  if (!user || user.role !== 'admin') {
    // Procurement users get bounced to their upload page instead of the admin-facing intelligence view
    if (user?.role === 'procurement') return <Navigate to="/procurement/rates" replace />;
    if (user?.role === 'stores') return <Navigate to="/stores/entry" replace />;
    return <Navigate to="/intelligence" replace />;
  }
  
  return children;
};

// Procurement Route wrapper — allows procurement OR admin
const ProcurementRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || !['procurement', 'admin'].includes(user.role)) {
    return <Navigate to="/intelligence" replace />;
  }
  return children;
};

// Stores Route wrapper — allows stores OR admin (same shape as ProcurementRoute)
const StoresRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || !['stores', 'admin'].includes(user.role)) {
    return <Navigate to="/intelligence" replace />;
  }
  return children;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'procurement') return <Navigate to="/procurement/rates" replace />;
    if (user.role === 'stores') return <Navigate to="/stores/entry" replace />;
    return <Navigate to="/intelligence" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      {/* Protected routes */}
      <Route element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route path="/reports" element={<ReportsListPage />} />
        <Route path="/reports/:id" element={<ReportViewPage />} />
        <Route path="/intelligence" element={<ExportIntelligenceReact />} />
        <Route path="/intelligence-legacy" element={<IntelligenceRedirect />} />
        <Route path="/competitive" element={<CompetitiveIntelligenceReact />} />
        <Route path="/competitive-legacy" element={<CompetitiveRedirect />} />
        <Route path="/explorer" element={<CompetitorExplorerReact />} />
        <Route path="/explorer-legacy" element={<CombinedRedirect />} />
        <Route path="/whitespace" element={<WhiteSpacePortsReact />} />
        <Route path="/whitespace-legacy" element={<WhiteSpaceRedirect />} />
        <Route path="/drilldown" element={<MarketDrilldownReact />} />
        <Route path="/drilldown-legacy" element={<DrilldownRedirect />} />
        <Route path="/buyers" element={<BuyersExplorerReact />} />
        <Route path="/buyers-legacy" element={<BuyersExplorerRedirect />} />
        <Route path="/invoice-tracker" element={<InvoicePITrackerPage />} />
        <Route path="/grade-strategy" element={<GradeStrategyReact />} />
        <Route path="/grade-strategy-legacy" element={<GradeStrategyPage />} />
        <Route path="/price-trend" element={<PriceTrendReact />} />
        <Route path="/price-trend-legacy" element={<PriceTrendPage />} />
        <Route path="/solar-project" element={<SolarProjectPage />} />
        <Route path="/market-watch" element={<MarketWatchPage />} />
        <Route path="/general-stores" element={<GeneralStoresPage />} />
        <Route path="/feed-plant" element={<FeedPlantPage />} />
        <Route path="/culture-store" element={<CultureStorePage />} />
        <Route path="/kapl" element={<KaplStorePage />} />
        <Route path="/rmfg" element={<RmfgPage />} />
        <Route path="/packing-store" element={<PackingStorePage />} />
        <Route path="/sil-pipeline" element={<SilPipelinePage />} />
        <Route path="/russia-register" element={<RussiaRegisterPage />} />
        <Route path="/stores/entry" element={<StoresRoute><StoresEntryPage /></StoresRoute>} />
        <Route path="/feed-plant/entry" element={<StoresRoute><FeedPlantEntryPage /></StoresRoute>} />
        <Route path="/culture-store/entry" element={<StoresRoute><CultureStoreEntryPage /></StoresRoute>} />
        <Route path="/kapl/entry" element={<StoresRoute><KaplStoreEntryPage /></StoresRoute>} />
        <Route path="/rmfg/entry" element={<StoresRoute><RmfgEntryPage /></StoresRoute>} />
        <Route path="/packing-store/entry" element={<StoresRoute><PackingStoreEntryPage /></StoresRoute>} />
        <Route path="/admin/data" element={<AdminRoute><AdminDataOpsPage /></AdminRoute>} />
        <Route path="/procurement/rates" element={<ProcurementRoute><ProcurementRatesPage /></ProcurementRoute>} />
        <Route path="/dashboard" element={<AdminRoute><DashboardPage /></AdminRoute>} />
        <Route path="/builder" element={<AdminRoute><DashboardBuilderPage /></AdminRoute>} />
        <Route path="/builder/:id" element={<AdminRoute><DashboardBuilderPage /></AdminRoute>} />
        <Route path="/upload" element={<AdminRoute><UploadPage /></AdminRoute>} />
        <Route path="/files" element={<AdminRoute><FilesPage /></AdminRoute>} />
        <Route path="/files/:id" element={<AdminRoute><FileDetailPage /></AdminRoute>} />
        <Route path="/chat" element={<AdminRoute><ChatPage /></AdminRoute>} />
        <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
        <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
      </Route>
      
      {/* Public shared dashboard - no auth needed */}
      <Route path="/shared/:token" element={<SharedDashboard />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/intelligence" replace />} />
      <Route path="*" element={<Navigate to="/intelligence" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // White-label: remove Emergent branding
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    hideBadge();
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
